import React from 'react';
import ErrorBoundaryState from 'app/components/mixins/ErrorBoundaryState';
import { useHistoryLocation } from 'app/components/router/History';
import { ResponseError } from 'app/constants/ResponseErrors';
import BadRequest from 'app/components/pages/BadRequest';
import Forbidden from 'app/components/pages/Forbidden';
import Maintenance from 'app/components/pages/Maintenance';
import NotFound from 'app/components/pages/NotFound';
import UnknownError from 'app/components/pages/UnknownError';
import createErrorType from 'app/utils/createErrorType';

/**
 * Can be thrown from a component directly to make ErrorBoundaryPage render a
 * 403 page.
 */
export class PageRestrictedError extends createErrorType('PageRestrictedError') {}
/**
 * Can be thrown from a component directly to make ErrorBoundaryPage render a
 * 404 page.
 */
export class NotFoundError extends createErrorType('NotFoundError') {}

const ErrorBoundaryPage: React.FunctionComponent<{
  fallbackErrorComponent?: React.ComponentType<{ error: Error | undefined }>,
}> = ({
  children,
  fallbackErrorComponent: /* as */ FallbackErrorComponent = UnknownError,
}) => {
  const location = useHistoryLocation();

  return (
    <ErrorBoundaryState
      errorKey={location.key}
    >{({ error }) => {
      if (error === undefined) {
        return children;
      } else {
        if (error instanceof ResponseError) {
          switch (error.status) {
            case 400: return <BadRequest />;
            case 404: return <NotFound />;
            case 403: return <Forbidden />;
            case 503: return <Maintenance />;
          }
        } else if (error instanceof PageRestrictedError) {
          return <Forbidden />;
        } else if (error instanceof NotFoundError) {
          return <NotFound />;
        }

        return <FallbackErrorComponent error={error} />;
      }
    }}</ErrorBoundaryState>
  );
};

export default ErrorBoundaryPage;
